import React, { useRef, useEffect, useState } from "react";
import { Camera, CameraOff, Eye, EyeOff } from "lucide-react";

interface CameraMonitorProps {
  stream: MediaStream | null;
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
  className?: string;
}

const CameraMonitor: React.FC<CameraMonitorProps> = ({
  stream,
  isMinimized = false,
  onToggleMinimize,
  className = "",
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play().catch(() => {});
        setIsVideoReady(true);
      };
    } else {
      setIsVideoReady(false);
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [stream]);

  if (!stream) {
    return (
      <div
        className={`bg-gray-900 text-white p-4 rounded-lg flex items-center justify-center ${className}`}
      >
        <div className="flex items-center space-x-2">
          <CameraOff className="w-5 h-5 text-red-500" />
          <span className="text-sm">Camera Not Available</span>
        </div>
      </div>
    );
  }

  const sizeClasses = isMinimized
    ? "w-32 h-24"
    : "w-64 h-48";

  return (
    <div
      className={`bg-gray-900 rounded-lg overflow-hidden relative ${sizeClasses} ${className}`}
    >
      {/* Video Element */}
      {isVisible ? (
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-full h-full object-cover mirror"
          style={{ transform: 'scaleX(-1)' }} // Mirror the video
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-800">
          <EyeOff className="w-8 h-8 text-gray-500" />
        </div>
      )}

      {/* Controls Overlay */}
      <div className="absolute top-2 right-2 flex space-x-1">
        {/* Toggle Visibility */}
        <button
          onClick={() => setIsVisible(!isVisible)}
          className="bg-black bg-opacity-50 text-white p-1.5 rounded hover:bg-opacity-70 transition-all"
          title={isVisible ? "Hide video" : "Show video"}
        >
          {isVisible ? (
            <Eye className="w-3 h-3" />
          ) : (
            <EyeOff className="w-3 h-3" />
          )}
        </button>

        {/* Toggle Size */}
        {onToggleMinimize && (
          <button
            onClick={onToggleMinimize}
            className="bg-black bg-opacity-50 text-white p-1.5 rounded hover:bg-opacity-70 transition-all"
            title={isMinimized ? "Expand" : "Minimize"}
          >
            {isMinimized ? (
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Status Indicator */}
      <div className="absolute bottom-2 left-2">
        <div className="flex items-center space-x-1">
          <div
            className={`w-2 h-2 rounded-full ${
              isVideoReady ? "bg-green-500" : "bg-red-500"
            }`}
          />
          {!isMinimized && (
            <span className="text-white text-xs">
              {isVideoReady ? "Recording" : "No Signal"}
            </span>
          )}
        </div>
      </div>

      {/* Camera Icon for Minimized State */}
      {isMinimized && (
        <div className="absolute top-2 left-2">
          <Camera className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  );
};

export default CameraMonitor;